/**
 * PageConfigPanel — F12.E T12.E.10.
 *
 * Zakładka „Strona" w ModuleSettingsPage.
 * Pozwala skonfigurować co jest widoczne na stronie detalu rekordu:
 *  - widoczność prawego panelu
 *  - zakładki prawego panelu (Historia, Załączniki)
 *  - (future: Komentarze, pola nagłówka)
 *
 * Konfiguracja przechowywana w localStorage per moduł
 * (klucz: `page-config-<moduleId>`).
 *
 * Eksportuje też hook `usePageConfig` — używany przez RecordDetailPage.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, Eye, EyeOff, Paperclip, RotateCcw } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PageConfig {
  rightPanel: {
    show: boolean;
    showHistory: boolean;
    showAttachments: boolean;
  };
}

const DEFAULT_CONFIG: PageConfig = {
  rightPanel: {
    show: true,
    showHistory: true,
    showAttachments: true,
  },
};

interface Props {
  moduleId: string;
}

// ─── localStorage helpers ─────────────────────────────────────────────────────

function storageKey(moduleId: string) {
  return `page-config-${moduleId}`;
}

function loadConfig(moduleId: string): PageConfig {
  if (!moduleId) return DEFAULT_CONFIG;
  try {
    const raw = localStorage.getItem(storageKey(moduleId));
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<PageConfig>;
      // Merge z domyślnym aby nie stracić nowych kluczy
      return {
        rightPanel: {
          ...DEFAULT_CONFIG.rightPanel,
          ...(parsed.rightPanel ?? {}),
        },
      };
    }
  } catch {
    // ignore
  }
  return { ...DEFAULT_CONFIG, rightPanel: { ...DEFAULT_CONFIG.rightPanel } };
}

function saveConfig(moduleId: string, config: PageConfig) {
  try {
    localStorage.setItem(storageKey(moduleId), JSON.stringify(config));
  } catch {
    // ignore
  }
}

/** Hook do odczytania konfiguracji strony detalu — używany przez RecordDetailPage. */
export function usePageConfig(moduleId: string): PageConfig {
  return loadConfig(moduleId);
}

// ─── Toggle Row ───────────────────────────────────────────────────────────────

interface ToggleRowProps {
  icon: React.ReactNode;
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}

function ToggleRow({ icon, label, checked, disabled, onChange }: ToggleRowProps) {
  return (
    <label
      className={`flex cursor-pointer items-center justify-between rounded-md px-3 py-2 transition-colors hover:bg-bg-secondary ${
        disabled ? 'opacity-50 cursor-not-allowed' : ''
      }`}
    >
      <div className="flex items-center gap-2 text-sm text-text-primary">
        <span className="text-text-muted">{icon}</span>
        {label}
      </div>
      <div className="relative">
        <input
          type="checkbox"
          className="sr-only"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
        {/* Toggle switch */}
        <div
          className={`h-5 w-9 rounded-full transition-colors ${
            checked && !disabled ? 'bg-accent-500' : 'bg-bg-tertiary'
          } ${disabled ? '' : 'cursor-pointer'}`}
        >
          <div
            className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
              checked ? 'translate-x-4' : 'translate-x-0.5'
            }`}
          />
        </div>
      </div>
    </label>
  );
}

// ─── Eye badge (visible / hidden summary) ─────────────────────────────────────

function VisibilityBadge({ visible }: { visible: boolean }) {
  const { t } = useTranslation();
  return visible ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-success-500/10 px-2 py-0.5 text-xs text-success-600">
      <Eye className="h-3 w-3" />
      {t('tenant.moduleSettings.page.visible')}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-bg-tertiary px-2 py-0.5 text-xs text-text-muted">
      <EyeOff className="h-3 w-3" />
      {t('tenant.moduleSettings.page.hidden')}
    </span>
  );
}

// ─── Main Panel ───────────────────────────────────────────────────────────────

export function PageConfigPanel({ moduleId }: Props) {
  const { t } = useTranslation();
  const [config, setConfig] = useState<PageConfig>(() => loadConfig(moduleId));

  // Zapisuj przy każdej zmianie
  useEffect(() => {
    saveConfig(moduleId, config);
  }, [moduleId, config]);

  function patch(partial: Partial<PageConfig['rightPanel']>) {
    setConfig((prev) => ({
      ...prev,
      rightPanel: { ...prev.rightPanel, ...partial },
    }));
  }

  function resetToDefaults() {
    setConfig({ ...DEFAULT_CONFIG, rightPanel: { ...DEFAULT_CONFIG.rightPanel } });
  }

  const { rightPanel } = config;

  return (
    <div className="space-y-6 py-2">
      {/* Prawy panel */}
      <section>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-text-primary">
            {t('tenant.moduleSettings.page.rightPanel.title')}
          </h3>
          <VisibilityBadge visible={rightPanel.show} />
        </div>

        <div className="overflow-hidden rounded-lg border border-border-primary bg-bg-primary">
          {/* Główny toggle — pokaż/ukryj cały prawy panel */}
          <ToggleRow
            icon={<Eye className="h-4 w-4" />}
            label={t('tenant.moduleSettings.page.rightPanel.showPanel')}
            checked={rightPanel.show}
            onChange={(v) => patch({ show: v })}
          />

          {/* Separator */}
          {rightPanel.show && (
            <div className="border-t border-border-primary">
              <p className="px-3 pb-1 pt-2 text-xs font-medium uppercase tracking-wider text-text-muted">
                {t('tenant.moduleSettings.page.rightPanel.tabs')}
              </p>

              <ToggleRow
                icon={<Clock className="h-4 w-4" />}
                label={t('tenant.moduleSettings.page.rightPanel.history')}
                checked={rightPanel.showHistory}
                onChange={(v) => patch({ showHistory: v })}
              />

              <ToggleRow
                icon={<Paperclip className="h-4 w-4" />}
                label={t('tenant.moduleSettings.page.rightPanel.attachments')}
                checked={rightPanel.showAttachments}
                onChange={(v) => patch({ showAttachments: v })}
              />
            </div>
          )}
        </div>

        {/* Ostrzeżenie gdy wszystkie zakładki wyłączone ale panel włączony */}
        {rightPanel.show && !rightPanel.showHistory && !rightPanel.showAttachments && (
          <p className="mt-2 text-xs text-warning-600">
            {t('tenant.moduleSettings.page.rightPanel.allTabsHidden')}
          </p>
        )}
      </section>

      {/* Footer reset */}
      <div className="flex items-center justify-end border-t border-border-primary pt-4">
        <button
          type="button"
          onClick={resetToDefaults}
          className="flex items-center gap-1.5 text-xs text-text-muted hover:text-text-primary"
        >
          <RotateCcw className="h-3 w-3" />
          {t('tenant.moduleSettings.page.reset')}
        </button>
      </div>
    </div>
  );
}
